import { ArrowBackRounded, BadgeRounded } from "@mui/icons-material";
import { Box, Card, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { PropsWithChildren, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useSurveyorsFilterStore } from "../../../../store/use-surveyors-filter-store";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

const TAB_SUBTITLES = [
  "Manage field surveyors and the surveys mapped to them.",
  "Manage surveys and view submitted responses.",
];

export default function SurveyorLayout({ children }: PropsWithChildren<{}>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, surveysPage, setPage, setSurveysPage, setCurrentTab, currentTab } =
    useSurveyorsFilterStore();

  const handleChange = (event: React.SyntheticEvent, currentTabVal: number) => {
    setCurrentTab(currentTabVal);
    setSearchParams({ currentTab: currentTabVal.toString() }, { replace: true });
  };

  const handleBackClick = () => {
    const currPage = Number(searchParams.get("page")) || 1;
    if (currPage > 1) {
      const newPage = currPage - 1;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("page", newPage.toString());
          return params;
        },
        { replace: true }
      );

      if (currentTab == 0) {
        setPage?.(newPage);
      } else {
        setSurveysPage(newPage);
      }
    }
  };

  useEffect(() => {
    if (Number(searchParams.get("currentTab")) == 0) {
      setPage(Number(searchParams.get("page")) || 1);
    } else {
      setSurveysPage(Number(searchParams.get("page")));
    }
    setCurrentTab(Number(searchParams.get("currentTab") || 0));
    setSearchParams(
      { page: searchParams.get("page") || "1", currentTab: searchParams.get("currentTab") || "0" },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        {((currentTab == 0 && page > 1) || (currentTab != 0 && surveysPage > 1)) && (
          <Tooltip title="Back to previous page">
            <IconButton onClick={handleBackClick} size="small">
              <ArrowBackRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: purpleLight,
              flexShrink: 0,
            }}
          >
            <BadgeRounded sx={{ color: purple, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color={black}>
              Surveyors
            </Typography>
            <Typography variant="body2" color={greyEnabled} mt={0.5}>
              {TAB_SUBTITLES[currentTab]}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Card
        elevation={0}
        sx={{
          display: "flex",
          mt: 2,
          width: "100%",
          minHeight: 600,
          mb: 2,
          borderRadius: 3,
          border: "1px solid #ECECEC",
        }}
      >
        <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: "1px solid #ECECEC", px: 3, pt: 1 }}>
            <Tabs
              value={currentTab}
              aria-label="surveyor tabs"
              onChange={handleChange}
              TabIndicatorProps={{
                style: { backgroundColor: purple, height: 3, borderRadius: 3 },
              }}
            >
              <Tab
                label="Surveyor"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: greyEnabled,
                  "&.Mui-selected": { color: purple },
                }}
              />
              <Tab
                label="Surveys"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: greyEnabled,
                  "&.Mui-selected": { color: purple },
                }}
              />
            </Tabs>
          </Box>
          {children}
        </Box>
      </Card>
    </Box>
  );
}
